import type {DiffEntry, DiffEntryData} from '@github-ui/diff-lines'
import type {UserDiffViewSettings} from '@github-ui/diff-view-settings/types'
import type {CommitExtended} from '@github-ui/commits/types/commit-types'
import type {FileFilterMenuOptions} from '../../components/diff-filtering/FileFilter'
import type {FileTreePayload, PullRequestFileTreeDiff} from './file-tree'
import type {Repository, PullRequest as ToolbarPullRequest} from './toolbar'
import type {PullRequest} from '../../types'
import type {Markers} from '../../stores/slices/markers'
import type {HeaderPageData, HeaderRepository, User} from './header'
import type {Codeowners} from './codeowners'
import type {PendingReview} from '../../stores/slices/pending-review'
import type {DiffFilterState} from '../../stores/slices/diff-filter'
import type {ChangeGroupPayload} from '../../types/group-types'
import type {DiffRange} from '@github-ui/diff-lines/types'

export const Notices = {
  SingleFileMode: 'single_file_mode',
  CompactDiffLines: 'compact_diff_lines',
  FilesChangedOptOut: 'files_changed_opt_out',
} as const

export type FilesPageNotices = (typeof Notices)[keyof typeof Notices]

export type UserNotice<T> = {
  name: T
  dismissed: boolean
}

export type PageLimits = {
  annotationsLimit: number
  annotationsLimitExceeded: boolean
  filesLimit: number
  filesLimitExceeded: boolean
  linesChangedLimit: number
  linesChangedLimitExceeded: boolean
  reviewThreadsLimit: number
  reviewThreadsLimitExceeded: boolean
  reviewThreadsPaginatedLimit: number
  reviewThreadsPaginatedLimitExceeded: boolean
}

export type FilesRoutePayloadUser = User & {
  canComment: boolean
  currentUserAvatarUrl?: string
  currentUserLogin?: string
  hasCopilotAccess: boolean
  canApplySuggestion: boolean
  isFileTreeExpanded: boolean
  lastReviewOid?: string
  shouldShowViewedFilesCount: boolean
  tabSize?: number
  viewSettings: UserDiffViewSettings & PullRequestUserDiffViewSettings
  viewedFilesCount: number
}

export type FilesRoutePayload = Omit<HeaderPageData, 'pullRequest' | 'repository' | 'user'> & {
  changeGroupGeneration: ChangeGroupPayload
  codeowners?: Codeowners
  /**
   * Commit information for single-commit views. Only populated when viewing changes for a specific commit
   */
  commit?: CommitExtended
  commits: FileTreePayload['commits']
  comparison: Comparison
  diffContents: DiffContents[]
  diffSummaries: PullRequestFileTreeDiff[]
  fileFilter: FileFilterPayload
  isSingleFileMode: boolean
  markers?: Markers
  pageLimits: PageLimits
  pullRequest: ToolbarPullRequest & PullRequest
  repository: HeaderRepository & Repository
  shouldLoadCodeowners: boolean
  user: FilesRoutePayloadUser
  userNotices?: Array<UserNotice<FilesPageNotices>>
  viewerPendingReview: PendingReview
  virtualizeDiffEntries: boolean
}

type FileFilterPayload = {
  initialState: DiffFilterState
  menuOptions: FileFilterMenuOptions
}

type PullRequestUserDiffViewSettings = {
  hideWhitespace: boolean
  showChangeGroups: boolean
}

// subset of DiffEntry returned by the API - we map in other shared fields from the rest of the payload as needed
export type DiffContents = DiffEntryData &
  Pick<DiffEntry, 'isBinary' | 'linesAdded' | 'linesDeleted' | 'richDiff' | 'diffSize' | 'reviewed' | 'changeType'>

export type Comparison = {
  fullDiff: DiffRange
  selectedRange?: DiffRange
  viewing: 'FULL' | 'COMMIT' | 'RANGE'
}
